import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errorMessage } from "../../lib/api";
import { COMMODITY_DATA } from "../../mock";
import { invalidateCupping } from "../../hooks/useCupping";

const LOTS = Object.values(COMMODITY_DATA).flatMap((c) => c.range.items.map((i) => ({ ...i, commodity: c.label })));
const inputCls = "w-full bg-transparent border-b border-[#1f4a33]/25 py-2 text-[#10271a] focus:outline-none focus:border-[#1f4a33]";

export default function CuppingPanel() {
  const [cupping, setCupping] = useState({});
  useEffect(() => { api.get("/cupping").then(({ data }) => setCupping(data)); }, []);
  return (
    <div data-testid="admin-cupping">
      <p className="text-[#7c5230] text-xs tracking-[0.25em] uppercase">Trade desk</p>
      <h1 className="mt-3 font-display text-4xl md:text-5xl font-light text-[#10271a]">Cupping notes</h1>
      <p className="mt-3 text-[#5e6b61] max-w-xl">Scores and notes publish instantly to the lot cards and spec sheets.</p>
      <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-5">
        {LOTS.map((lot) => <LotEditor key={lot.id} lot={lot} value={cupping[lot.id]} onSaved={(d) => setCupping((c) => ({ ...c, [lot.id]: d }))} />)}
      </div>
    </div>
  );
}

function LotEditor({ lot, value, onSaved }) {
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setForm({ score: value?.score ?? "", scale: value?.scale ?? "SCA / 100", notes: value?.notes ?? "", cupper: value?.cupper ?? "", cupped_on: value?.cupped_on ?? new Date().toISOString().slice(0, 10) }); }, [value]);
  if (!form) return null;
  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.put(`/admin/cupping/${lot.id}`, { ...form, score: Number(form.score) });
      invalidateCupping();
      onSaved(data);
      toast.success(`${lot.name} updated`);
    } catch (err) { toast.error(errorMessage(err)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={save} className="rounded-3xl border border-[#10271a]/10 bg-[#faf6ec] p-6" data-testid={`cupping-editor-${lot.id}`}>
      <div className="flex items-center gap-4">
        <img src={lot.image} alt="" className="h-12 w-12 rounded-xl object-cover" />
        <div>
          <p className="text-[#7c5230] text-[10px] tracking-[0.18em] uppercase">{lot.commodity} · {lot.origin}</p>
          <h3 className="font-display text-2xl text-[#10271a] leading-none mt-1">{lot.name}</h3>
        </div>
      </div>
      <div className="mt-5 grid grid-cols-[100px_1fr] gap-4">
        <label className="block"><span className="text-[10px] tracking-[0.18em] uppercase text-[#5e6b61]">Score</span><input type="number" step="0.1" min="0" max="100" className={`${inputCls} font-display text-2xl`} value={form.score} onChange={update("score")} data-testid={`cupping-score-${lot.id}`} /></label>
        <label className="block"><span className="text-[10px] tracking-[0.18em] uppercase text-[#5e6b61]">Scale</span><input className={inputCls} value={form.scale} onChange={update("scale")} data-testid={`cupping-scale-${lot.id}`} /></label>
      </div>
      <label className="block mt-4"><span className="text-[10px] tracking-[0.18em] uppercase text-[#5e6b61]">Notes</span><textarea rows={3} className={`${inputCls} resize-none`} value={form.notes} onChange={update("notes")} data-testid={`cupping-notes-${lot.id}`} /></label>
      <div className="mt-4 grid grid-cols-2 gap-4">
        <label className="block"><span className="text-[10px] tracking-[0.18em] uppercase text-[#5e6b61]">Cupper</span><input className={inputCls} value={form.cupper} onChange={update("cupper")} data-testid={`cupping-cupper-${lot.id}`} /></label>
        <label className="block"><span className="text-[10px] tracking-[0.18em] uppercase text-[#5e6b61]">Cupped on</span><input type="date" className={inputCls} value={form.cupped_on} onChange={update("cupped_on")} data-testid={`cupping-date-${lot.id}`} /></label>
      </div>
      <div className="mt-5 flex items-center justify-between">
        <span className="text-xs text-[#5e6b61]">{value?.updated_at ? `Updated ${new Date(value.updated_at).toLocaleDateString("en-GB")}` : "Not yet cupped"}</span>
        <button type="submit" disabled={busy} data-testid={`cupping-save-${lot.id}`} className="bg-[#1f4a33] text-[#f6f1e7] px-5 py-2.5 rounded-full text-[12px] tracking-[0.12em] uppercase font-semibold hover:bg-[#10271a] disabled:opacity-60">{busy ? "Saving..." : "Save & publish"}</button>
      </div>
    </form>
  );
}
